import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { clientService } from '../../services';
import { notifications } from '../../utils/toast';

const PLAN_HIERARCHY = { free: 0, pro: 1, pro_plus: 2, enterprise_starter: 3, enterprise_scale: 4 };

const PLAN_NAMES = {
  free: 'Free Trial',
  pro: 'Pro',
  pro_plus: 'Pro Plus',
  enterprise_starter: 'Enterprise Starter',
  enterprise_scale: 'Enterprise Scale',
};

/**
 * Pricing Page
 * Shows available subscription plans with same design as SubscriptionPage
 */

const PricingPage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [subscription, setSubscription] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Upgrade/downgrade modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedPlanForAction, setSelectedPlanForAction] = useState(null);
  const [downgradeLoading, setDowngradeLoading] = useState(false);

  // Plans data with monthly and annual pricing (Updated as per PDF)
  const plans = [
    {
      id: 'free',
      name: 'Free Trial',
      monthlyPrice: 0,
      description: 'Experience Aktivity in a real training session',
      features: [
        '3 sessions total (one-time)',
        'Up to 30 participants/session',
        'All core features',
      ],
      buttonLabel: 'Start Free',
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 1999,
      description: 'For trainers who want proof of impact',
      features: [
        '20 sessions/month',
        '30 participants/session',
        '1 trainer access',
      ],
      buttonLabel: 'Choose Pro',
    },
    {
      id: 'pro_plus',
      name: 'Pro Plus',
      monthlyPrice: 5999,
      description: 'Learning intelligence at scale',
      features: [
        'Unlimited sessions',
        '100 participants/session',
        'Up to 3 trainer accounts',
        'Priority support',
      ],
      buttonLabel: 'Go Pro Plus',
    },
  ];

  // Enterprise plans data
  const enterprisePlans = [
    {
      id: 'enterprise_starter',
      name: 'Enterprise Starter',
      price: 180000, // ₹1,80,000/year
      description: 'For L&D Teams & Training Companies',
      features: [
        '200+ participants/session',
        'Up to 5 trainer accounts',
        'Unlimited sessions',
        'Live dashboards',
        'Trainer leaderboards',
        'Program-level learning intelligence',
      ],
      buttonLabel: 'Choose Enterprise Starter',
    },
    {
      id: 'enterprise_scale',
      name: 'Enterprise Scale',
      price: 0, // Custom pricing
      isCustom: true,
      description: 'Talk to Sales',
      features: [],
      addOns: [
        { label: 'Additional Trainer', price: '₹1,000/mo | $20/mo' },
        { label: 'Extra participants', price: 'Custom pricing' },
        { label: 'White-label & advanced analytics', price: '' },
      ],
      buttonLabel: 'Talk to Sales',
    },
  ];

  useEffect(() => {
    const fetchCurrentSubscription = async () => {
      try {
        setLoading(true);
        const response = await clientService.getCurrentSubscription();
        if (response.success && response.data) {
          setSubscription(response.data);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentSubscription();
  }, []);

  // Get price for monthly plans (Pro, Pro Plus are monthly only)
  const getPlanPrice = (plan) => {
    if (plan.monthlyPrice === 0) return { price: 0, interval: 'forever', savings: 0 };
    return { price: plan.monthlyPrice, interval: 'month', savings: 0 };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleSelectPlan = async (planId) => {
    // If selecting free plan, just navigate
    if (planId === 'free') {
      navigate(`/client/subscription`);
      return;
    }

    // Enterprise Scale - Custom pricing, contact sales
    if (planId === 'enterprise_scale') {
      window.location.href = 'mailto:sales@aktivity.io?subject=Enterprise Scale Inquiry&body=Hi, I am interested in the Enterprise Scale plan. Please contact me to discuss pricing and features.';
      return;
    }

    const currentPlan = subscription?.planId || 'free';
    const currentLevel = PLAN_HIERARCHY[currentPlan] ?? 0;
    const selectedLevel = PLAN_HIERARCHY[planId] ?? 0;

    if (selectedLevel > currentLevel) {
      // Upgrade - show confirmation then redirect to checkout
      setSelectedPlanForAction(planId);
      setShowUpgradeModal(true);
    } else if (selectedLevel < currentLevel) {
      // Downgrade - show confirmation then schedule downgrade
      setSelectedPlanForAction(planId);
      setShowDowngradeModal(true);
    }
  };

  const handleConfirmUpgrade = async () => {
    const planId = selectedPlanForAction;
    setShowUpgradeModal(false);

    try {
      setCheckoutLoading(true);
      // Enterprise plans are yearly, all others are monthly only
      const cycle = planId.startsWith('enterprise') ? 'yearly' : 'monthly';

      localStorage.setItem('pending_plan_id', planId);
      localStorage.setItem('pending_billing_cycle', cycle);

      const response = await clientService.createCheckoutSession(planId, cycle);

      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        notifications.error('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      notifications.error(error.response?.data?.error || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleConfirmDowngrade = async () => {
    const planId = selectedPlanForAction;

    try {
      setDowngradeLoading(true);
      const response = await clientService.scheduleDowngrade(planId);

      if (response.success) {
        setShowDowngradeModal(false);
        notifications.success(`Your plan will be downgraded to ${PLAN_NAMES[planId]} at the end of your billing period.`);
        navigate('/client/subscription');
      }
    } catch (error) {
      console.error('Downgrade error:', error);
      notifications.error(error.response?.data?.error || 'Failed to schedule downgrade. Please try again.');
    } finally {
      setDowngradeLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto pb-20 sm:pb-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-2">Choose the Right Plan for You</h1>
          <p className="text-xs sm:text-sm text-neutral-600">
            Select a plan that fits your training needs. Upgrade or downgrade anytime.
          </p>

          {/* Plan Type Toggle */}
          <div className="mt-6 inline-flex items-center gap-3 bg-neutral-100 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-md font-medium text-xs sm:text-sm transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Monthly Plans
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-md font-medium text-xs sm:text-sm transition-all ${
                billingCycle === 'annual'
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Enterprise Plans
            </button>
          </div>
        </div>

        {/* Monthly Plans - Free, Pro, Pro Plus */}
        {billingCycle === 'monthly' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {plans.map((plan, index) => {
              const priceInfo = getPlanPrice(plan);
              const isCurrentPlan = subscription?.planId === plan.id;
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className={`relative bg-white rounded-2xl border-2 p-4 sm:p-6 flex flex-col ${
                    isCurrentPlan
                      ? 'border-orange-400 shadow-lg shadow-orange-100'
                      : 'border-neutral-200 hover:border-neutral-300'
                  } transition-all`}
                >
                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        Current Plan
                      </span>
                    </div>
                  )}

                  <div className="flex-1">
                    <div className="text-center mb-6">
                      <h3 className="text-base sm:text-xl font-bold text-neutral-900 mb-2">{plan.name}</h3>
                      <p className="text-xs sm:text-sm text-neutral-600 mb-4">{plan.description}</p>

                      {/* Price Display */}
                      <div className="mb-2">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-xl sm:text-3xl font-bold text-neutral-900">
                            ₹{priceInfo.price.toLocaleString('en-IN')}
                          </span>
                          <span className="text-neutral-500">/{priceInfo.interval}</span>
                          {priceInfo.price > 0 && (
                            <span className="text-xs text-neutral-400">+GST</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-neutral-700">
                          <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    className={`w-full py-3 px-4 rounded-lg font-semibold text-xs sm:text-sm transition-all mt-auto ${
                      subscription?.planId === plan.id
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white cursor-default'
                        : 'bg-neutral-800 hover:bg-neutral-700 text-white'
                    }`}
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={subscription?.planId === plan.id || checkoutLoading}
                  >
                    {checkoutLoading ? 'Redirecting...' : subscription?.planId === plan.id ? 'Current Plan' : plan.buttonLabel}
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Enterprise Plans Section */}
        {billingCycle === 'annual' && (
          <div>
            {/* Section Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-neutral-800 text-white text-xs font-semibold px-4 py-2 rounded-full mb-3">
                ANNUAL PLANS
              </div>
              <h3 className="text-base sm:text-xl font-bold text-neutral-900">For L&D Teams & Training Companies</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch">
              {enterprisePlans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className="relative bg-white rounded-2xl border-2 border-neutral-200 hover:border-neutral-300 p-4 sm:p-6 transition-all flex flex-col"
                >
                  <div className="flex-1">
                    <div className="text-center mb-6">
                      <h3 className="text-base sm:text-xl font-bold text-neutral-900 mb-2">{plan.name}</h3>
                      <p className="text-xs sm:text-sm text-neutral-600 mb-4">{plan.description}</p>

                      {/* Price Display */}
                      <div className="mb-2">
                        {plan.isCustom ? (
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-lg sm:text-2xl font-bold text-neutral-900">Custom Pricing</span>
                          </div>
                        ) : (
                          <div className="flex items-baseline justify-center gap-1">
                            <span className="text-xl sm:text-3xl font-bold text-neutral-900">
                              ₹{plan.price.toLocaleString('en-IN')}
                            </span>
                            <span className="text-neutral-500">/year</span>
                            <span className="text-xs text-neutral-400">+GST</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-neutral-700">
                          <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* Add-ons Section for Enterprise Scale */}
                    {plan.addOns && plan.addOns.length > 0 && (
                      <div className="mb-6 pt-4 border-t border-neutral-200">
                        <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">Available Add-ons</h4>
                        <ul className="space-y-2">
                          {plan.addOns.map((addOn, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-xs text-neutral-600">
                              <svg className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                              </svg>
                              <div>
                                <span className="font-medium text-neutral-700">{addOn.label}:</span>{' '}
                                <span className="text-neutral-500">{addOn.price}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <button
                    className="w-full py-3 px-4 rounded-lg font-semibold text-xs sm:text-sm transition-all bg-neutral-800 hover:bg-neutral-700 text-white disabled:opacity-50 mt-auto"
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? 'Redirecting...' : plan.buttonLabel}
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-2">Frequently Asked Questions</h2>
            <p className="text-xs sm:text-sm text-neutral-600">Everything you need to know about Aktivity pricing and subscriptions</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: 'Is Aktivity free to use?',
                answer: 'Yes! Aktivity offers a Free Trial plan with 3 sessions (one-time) to help you experience the platform in a real training session. You can access all core features with up to 30 participants per session.',
              },
              {
                question: 'What happens after my Free Trial ends?',
                answer: 'After your 3 free sessions are used, you\'ll need to upgrade to a paid plan to continue creating sessions. Your existing session data and reports will remain accessible.',
              },
              {
                question: 'Do all plans include the same features?',
                answer: 'Yes. Every plan includes all core learning features, including reflections, challenges, action plans, feedback, and reports. Pricing only changes based on: 1. Number of sessions 2. Participant volume 3. Number of trainer accounts. Higher-tier plans also include live dashboards, trainer leaderboards, and priority support.',
              },
              {
                question: 'What does "Trainer Access" mean?',
                answer: 'Trainer Access refers to the number of trainer accounts that can create and manage training sessions under your organization. Free and Pro plans include 1 trainer, Pro Plus includes up to 3 trainers, and Enterprise plans offer more.',
              },
              {
                question: 'Can I add more trainers to my plan?',
                answer: 'Yes! Enterprise Scale plan allows you to add additional trainer accounts at ₹1,000/month (India) or $20/month (Global). For other plans, you would need to upgrade to a higher tier.',
              },
              {
                question: 'Is Aktivity an LMS?',
                answer: 'No, Aktivity is not a traditional Learning Management System (LMS). It\'s a learning engagement and intelligence platform designed to make training sessions interactive, measure real-time engagement, and provide actionable insights for trainers.',
              },
              {
                question: 'Can Aktivity be used for in-person training?',
                answer: 'Yes! Aktivity works great for both virtual and in-person training sessions. Participants can join from their mobile devices or laptops, making it perfect for classroom settings, workshops, and live events.',
              },
              {
                question: 'How is Aktivity different from tools like Slido or Mentimeter?',
                answer: 'While Slido and Mentimeter focus primarily on polls and Q&A, Aktivity is designed specifically for training sessions with features like learning engagement tracking, comprehensive session reports, trainer performance analytics, and learning intelligence dashboards.',
              },
              {
                question: 'What billing options are available?',
                answer: 'Pro and Pro Plus plans are billed monthly. Enterprise Starter is billed annually at ₹1,80,000/year. Enterprise Scale has custom pricing — contact our sales team for details.',
              },
              {
                question: 'Why isn\'t Enterprise pricing listed?',
                answer: 'Enterprise Scale pricing is customized based on your organization\'s specific needs, including the number of trainers, expected participants, integration requirements, and support level. Contact our sales team for a personalized quote.',
              },
              {
                question: 'Is my data secure?',
                answer: 'Absolutely. Aktivity uses industry-standard encryption for all data transmission and storage. We are committed to protecting your data and comply with relevant data protection regulations. Your session data and participant information are kept secure and private.',
              },
              {
                question: 'Can I upgrade or downgrade my plan?',
                answer: 'Yes, you can change your plan at any time. When upgrading, you\'ll get immediate access to new features. When downgrading, the change takes effect at the end of your current billing period, and you\'ll retain access to your current plan until then.',
              },
            ].map((faq, index) => (
              <details
                key={index}
                className="group bg-white border border-neutral-200 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between p-3 sm:p-5 cursor-pointer hover:bg-neutral-50 transition-colors">
                  <span className="font-medium text-neutral-900 pr-4">{faq.question}</span>
                  <svg
                    className="w-5 h-5 text-neutral-500 flex-shrink-0 transition-transform group-open:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-3 sm:px-5 pb-3 sm:pb-5 text-xs sm:text-sm text-neutral-600 border-t border-neutral-100 pt-4">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>

          {/* Contact Support */}
          <div className="text-center mt-8 pt-8 border-t border-neutral-200">
            <p className="text-neutral-900 font-medium mb-2">Still have questions?</p>
            <p className="text-xs sm:text-sm text-neutral-600 mb-4">Talk to us — we'll help you choose the right plan for your training scale.</p>
            <div className="flex items-center justify-center gap-4">
              <a
                href="mailto:support@aktivity.io"
                className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Support
              </a>
              <Button variant="ghost" onClick={() => navigate('/client/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Confirmation Modal */}
      <Modal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} title="Confirm Upgrade" size="md">
        <div>
          <div className="mb-6">
            <p className="text-neutral-900 font-semibold mb-2">
              Upgrade to {PLAN_NAMES[selectedPlanForAction]}
            </p>
            <p className="text-xs sm:text-sm text-neutral-600 mb-4">
              You'll be redirected to a secure checkout to complete your upgrade. Your current subscription will be cancelled and you'll be charged for the new plan immediately.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-xs sm:text-sm text-amber-800">
                <strong>Note:</strong> Your remaining days on the current plan are not prorated. The new plan starts immediately after payment.
              </p>
            </div>

            {subscription && (
              <div className="bg-neutral-50 rounded-lg p-4">
                <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
                  <span className="text-neutral-600">Current Plan</span>
                  <span className="font-medium text-neutral-900">{PLAN_NAMES[subscription.planId] || 'Free'}</span>
                </div>
                <div className="flex items-center justify-between text-xs sm:text-sm">
                  <span className="text-neutral-600">New Plan</span>
                  <span className="font-medium text-primary-600">{PLAN_NAMES[selectedPlanForAction]}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowUpgradeModal(false)} fullWidth>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmUpgrade} disabled={checkoutLoading} fullWidth>
              {checkoutLoading ? 'Redirecting...' : 'Proceed to Checkout'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Downgrade Confirmation Modal */}
      <Modal isOpen={showDowngradeModal} onClose={() => setShowDowngradeModal(false)} title="Confirm Downgrade" size="md">
        <div>
          <div className="mb-6">
            <p className="text-neutral-900 font-semibold mb-2">
              Downgrade to {PLAN_NAMES[selectedPlanForAction]}
            </p>
            <p className="text-xs sm:text-sm text-neutral-600 mb-4">
              Your plan will change to {PLAN_NAMES[selectedPlanForAction]} at the end of your current billing period.
              You'll keep full access to your current plan until then.
            </p>

            {subscription && (
              <div className="bg-neutral-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
                  <span className="text-neutral-600">Current Plan</span>
                  <span className="font-medium text-neutral-900">{PLAN_NAMES[subscription.planId] || 'Free'}</span>
                </div>
                <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
                  <span className="text-neutral-600">New Plan (after period ends)</span>
                  <span className="font-medium text-amber-600">{PLAN_NAMES[selectedPlanForAction]}</span>
                </div>
                {subscription.currentPeriodEnd && (
                  <div className="flex items-center justify-between text-xs sm:text-sm">
                    <span className="text-neutral-600">Change Takes Effect</span>
                    <span className="font-medium text-neutral-900">{formatDate(subscription.currentPeriodEnd)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-xs sm:text-sm font-semibold text-amber-900 mb-2">You may lose access to:</p>
              <ul className="text-xs sm:text-sm text-amber-700 space-y-1">
                <li>- Higher session limits</li>
                <li>- Additional trainer accounts</li>
                <li>- Priority support</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setShowDowngradeModal(false)} fullWidth>
              Keep Current Plan
            </Button>
            <Button variant="danger" onClick={handleConfirmDowngrade} disabled={downgradeLoading} fullWidth>
              {downgradeLoading ? 'Scheduling...' : 'Confirm Downgrade'}
            </Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
};

export default PricingPage;
